import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type OpenAssetModalLocations, useTrackOpenedAssetModal } from '@air/analytics';
import { ClipType } from '@air/api/types';

import { calculateRankForItem } from '~/analytics/rankUtils';
import { QueryParamNames, type QueryType } from '~/constants/search';
import { usePublicAssetModalNavigation } from '~/providers/PublicAssetModalNavigationProvider';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { setCentralizedClipAction } from '~/store/centralizedClip/actions';
import { toCentralizedClipBoard } from '~/store/centralizedClip/toCentralizedClipBoard';
import { useAirStore } from '~/utils/ReduxUtils';

/**
 * Public-area owner of asset-page navigation.
 *
 * This is a deliberate copy of the private twin (`useGoToPrivateAssetPage`) per
 * rules/area-isolation.md: URL schemes are per-area architecture, so each area owns its own
 * navigation rather than sharing an app-generic hook. The contracts are free to diverge — do not
 * re-fuse them.
 *
 * This hook is surface-blind: it seeds the modal's clip and tracks the open, then hands navigation
 * to the mounting surface's PublicAssetModalNavigationProvider — a route push on the URL-backed
 * surfaces, editor state in the collection link editor's Edit tab.
 */
export type GoToPublicAssetPageProps = {
  asset: Parameters<typeof setCentralizedClipAction>[0]['clip'];
  query?: QueryType;
  trackLocation?: OpenAssetModalLocations;
  isAssetModalAlreadyOpen?: boolean;
};

const getQueryParamTimestamp = (asset: GoToPublicAssetPageProps['asset']): string | null =>
  typeof asset.assets?.spriteSheet !== 'undefined' &&
  asset.type === ClipType.video &&
  asset.position?.start.timestamp !== undefined
    ? String(asset.position.start.timestamp)
    : null;

export const useGoToPublicAssetPage = () => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { trackOpenedAssetModal } = useTrackOpenedAssetModal();
  const { activeClipId, onOpenAssetModal } = usePublicAssetModalNavigation();

  const goToAssetPage = useCallback(
    ({ asset, query, trackLocation = 'not-set', isAssetModalAlreadyOpen = false }: GoToPublicAssetPageProps) => {
      const navigationQuery = {
        [QueryParamNames.timestamp]: getQueryParamTimestamp(asset),
        ...query,
      };

      // Re-clicking the already-open asset is a no-op unless the caller is updating query
      // (e.g. switching Info vs Comments). Never fire a phantom "opened" event in that case.
      if (activeClipId === asset.id) {
        if (query) {
          onOpenAssetModal({
            clipId: asset.id,
            query,
          });
        }

        return;
      }

      // Seed the modal with the clicked card's clip so it renders immediately while the full
      // record fetches. Gallery clips omit boards; attach the board in the URL until that fetch.
      const board = centralizedBoardSelector(store.getState());

      dispatch(
        setCentralizedClipAction({
          clip: {
            boards: board ? [toCentralizedClipBoard(board)] : [],
            ...asset,
          },
        }),
      );

      onOpenAssetModal({
        clipId: asset.id,
        query: navigationQuery,
      });

      if (!isAssetModalAlreadyOpen) {
        const rank = calculateRankForItem(store, asset.id);

        trackOpenedAssetModal({
          location: trackLocation,
          ...(rank !== undefined && { rank }),
        });
      }
    },
    [activeClipId, dispatch, onOpenAssetModal, store, trackOpenedAssetModal],
  );

  return {
    goToAssetPage,
  };
};
